import { formatMoney } from '@theme/utilities';

if (!customElements.get('sc-purchase-close')) {
  customElements.define('sc-purchase-close', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.submitButton = this.querySelector('[data-sc-close-submit]');
      this.label = this.querySelector('[data-sc-close-label]');
      this.price = this.querySelector('[data-sc-close-price]');
      this.selection = this.querySelector('[data-sc-close-variant]');
      this.status = this.querySelector('[data-sc-close-status]');
      this.editButton = this.querySelector('[data-sc-close-edit]');
      this.readyLabel ??= this.dataset.addLabel || this.label?.textContent.trim() || '';
      this.pendingVariant = false;
      this.awaitingSubmission = false;
      this.cartConfirmed = false;
      this.productForm = null;
      this.added = false;
      const options = { signal: this.controller.signal };
      this.submitButton?.addEventListener('click', () => this.submit(), options);
      this.editButton?.addEventListener('click', () => this.editSelection(), options);
      document.addEventListener('cart:updated', event => this.cartUpdated(event), options);
      document.addEventListener('toast:open', event => {
        if (!this.awaitingSubmission || event.detail?.type !== 'error') return;
        this.awaitingSubmission = false;
        this.cartConfirmed = false;
        if (this.status) this.status.textContent = this.dataset.errorLabel || '';
        this.sync();
      }, options);
      document.addEventListener('shopify:section:load', () => this.bindForm(), options);
      this.bindForm();
      customElements.whenDefined('product-form').then(() => {
        if (this.isConnected) this.bindForm();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.formController?.abort();
      this.observer?.disconnect();
      clearTimeout(this.addedTimeout);
      this.awaitingSubmission = false;
      this.cartConfirmed = false;
    }

    findForm() {
      if (!this.dataset.productId) return null;
      const sectionId = this.dataset.mainSectionId;
      const section = sectionId ? document.getElementById(sectionId) || document.getElementById('shopify-section-' + sectionId) : document.querySelector('.shopify-section-product-section');
      if (!section) return null;
      return Array.from(section.querySelectorAll('product-form[data-product-id]')).find(form => form.dataset.productId === this.dataset.productId && !form.closest('product-card, quick-add-dialog, quick-add-component')) || null;
    }

    bindForm() {
      const next = this.findForm();
      if (next === this.productForm) {
        this.sync();
        return;
      }
      this.formController?.abort();
      this.observer?.disconnect();
      this.productForm = next;
      this.pendingVariant = false;
      this.awaitingSubmission = false;
      this.cartConfirmed = false;
      this.resetAdded();
      if (!next) {
        this.sync();
        return;
      }
      this.formController = new AbortController();
      const options = { signal: this.formController.signal };
      next.addEventListener('variant:selected', () => {
        this.pendingVariant = true;
        this.resetAdded();
        this.sync();
      }, options);
      next.addEventListener('variant:updated', () => {
        this.pendingVariant = false;
        this.sync();
      }, options);
      next.addEventListener('form:updated', () => this.sync(), options);
      next.addEventListener('change', () => {
        this.resetAdded();
        this.sync();
      }, options);
      next.addEventListener('input', () => {
        this.resetAdded();
        this.sync();
      }, options);
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(next, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class', 'value', 'checked', 'selected', 'data-price'] });
      this.sync();
    }

    nativeForm() {
      return this.productForm?.querySelector('form[action$="/cart/add"]');
    }

    nativeButton() {
      return this.productForm?.querySelector('[data-ref="add-to-cart-button"]');
    }

    nativeGroups() {
      return Array.from(this.productForm?.querySelectorAll('variant-picker .variant-picker__option') || []).map(group => {
        const select = group.querySelector('select');
        const inputs = select ? Array.from(select.options) : Array.from(group.querySelectorAll('input[type="radio"]'));
        const chosen = select ? select.selectedOptions[0] : inputs.find(input => input.checked);
        return { select, inputs, chosen, name: (select?.name || inputs[0]?.name || '').replace(/^option-/, '') };
      }).filter(group => group.inputs.length);
    }

    needsSelection() {
      if (!this.nativeForm()?.querySelector('input[name="id"]')?.value) return true;
      return this.nativeGroups().some(group => !group.chosen || group.chosen.value === '');
    }

    selectedValues() {
      const values = this.nativeGroups().map(group => group.chosen ? [group.name, group.chosen.value].filter(Boolean).join(' ') : '').filter(Boolean);
      const quantity = Number.parseInt(this.nativeForm()?.querySelector('input[name="quantity"]')?.value, 10) || 1;
      if (quantity > 1) values.push('×' + quantity);
      return values.join(' · ');
    }

    currentPrice(native) {
      let price;
      const currentQuantity = this.nativeForm()?.querySelector('input[name="quantity"]');
      const cachedQuantity = this.productForm?.quantitySelectorInput;
      const refreshQuantity = currentQuantity && this.productForm && 'quantitySelectorInput' in this.productForm && currentQuantity !== cachedQuantity;
      try {
        if (refreshQuantity) this.productForm.quantitySelectorInput = currentQuantity;
        price = this.productForm?.getCurrentTotalPrice?.()?.price;
      } catch {
      } finally {
        if (refreshQuantity) this.productForm.quantitySelectorInput = cachedQuantity;
      }
      if (Number.isFinite(price)) return price;
      const base = Number.parseInt(native?.closest('[data-ref="add-to-cart-button-container"]')?.dataset.price, 10);
      const quantity = Number.parseInt(this.nativeForm()?.querySelector('input[name="quantity"]')?.value, 10) || 1;
      return Number.isFinite(base) ? base * quantity : null;
    }

    sync() {
      if (!this.submitButton) return;
      const native = this.nativeButton();
      const loading = native?.classList.contains('is-loading') || false;
      if (this.awaitingSubmission && this.cartConfirmed && !loading) {
        this.awaitingSubmission = false;
        this.cartConfirmed = false;
        this.added = true;
        if (this.status) this.status.textContent = this.dataset.addedLabel || '';
        clearTimeout(this.addedTimeout);
        this.addedTimeout = setTimeout(() => {
          this.resetAdded();
          this.sync();
        }, 3500);
      }
      const unavailable = native?.disabled && !this.pendingVariant && !loading;
      const choose = native && !unavailable && this.needsSelection();
      const state = !native ? 'unbound' : this.pendingVariant ? 'pending' : loading ? 'loading' : unavailable ? 'unavailable' : choose ? 'choose' : this.added ? 'added' : 'ready';
      this.dataset.state = state;
      this.submitButton.disabled = !native || this.pendingVariant || loading || unavailable;
      this.submitButton.setAttribute('aria-busy', String(this.pendingVariant || loading));
      if (this.label) this.label.textContent = state === 'pending' || state === 'loading' ? this.dataset.loadingLabel || this.readyLabel : state === 'unavailable' || state === 'unbound' ? this.dataset.unavailableLabel || this.readyLabel : state === 'choose' ? this.dataset.chooseLabel || this.readyLabel : state === 'added' ? this.dataset.addedLabel || this.readyLabel : this.readyLabel;
      if (this.selection) this.selection.textContent = this.selectedValues();
      if (this.editButton) this.editButton.hidden = !this.productForm?.querySelector('variant-picker');
      const amount = this.currentPrice(native);
      if (this.price && Number.isFinite(amount)) this.price.innerHTML = formatMoney(amount);
    }

    resetAdded() {
      clearTimeout(this.addedTimeout);
      this.added = false;
      if (this.status) this.status.textContent = '';
    }

    cartUpdated(event) {
      if (!this.awaitingSubmission || event.detail?.data?.source !== 'product-form') return;
      const data = event.detail.data;
      if (data.productId && String(data.productId) !== this.dataset.productId) return;
      if (data.didError || event.detail.resource?.errors || event.detail.resource?.status >= 400) {
        this.awaitingSubmission = false;
        this.cartConfirmed = false;
        if (this.status) this.status.textContent = this.dataset.errorLabel || '';
      } else {
        this.cartConfirmed = true;
      }
      this.sync();
    }

    reveal(element) {
      if (!element) return;
      const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      element.scrollIntoView({ behavior: motion, block: 'center' });
      element.focus?.({ preventScroll: true });
    }

    editSelection() {
      const picker = this.productForm?.querySelector('variant-picker');
      if (!picker) return;
      const focusable = picker.querySelector('select:not(:disabled), input[type="radio"]:checked:not(:disabled)') || picker.querySelector('input[type="radio"]:not(:disabled)');
      this.reveal(focusable || picker);
    }

    submit() {
      const current = this.findForm();
      if (current !== this.productForm) this.bindForm();
      const native = this.nativeButton();
      const form = this.nativeForm();
      if (!native || !form || native.disabled || this.pendingVariant || native.classList.contains('is-loading')) return;
      if (this.needsSelection()) {
        this.editSelection();
        return;
      }
      if (form.checkValidity && !form.checkValidity()) {
        form.reportValidity?.();
        this.reveal(form.querySelector(':invalid'));
        return;
      }
      this.resetAdded();
      this.awaitingSubmission = true;
      this.cartConfirmed = false;
      native.click();
      this.sync();
      if (!native.classList.contains('is-loading')) this.awaitingSubmission = false;
    }
  });
}
